(() => {
  const ROOT_SELECTOR = '[data-hr-product-merch-owned]';
  const initializedRoots = new WeakSet();

  const parseConfig = (root) => {
    try {
      return JSON.parse(root.querySelector('[data-hr-product-merch-config]')?.textContent || '{}');
    } catch (error) {
      console.error('High Rider merch product configuration is invalid.', error);
      return {};
    }
  };

  const setCardLink = (root, rid, url, label) => {
    const card = root.querySelector(`[data-rid="${rid}"]`);
    if (!card || !url) return;
    card.setAttribute('role', 'link');
    card.setAttribute('tabindex', '0');
    card.setAttribute('aria-label', label);
    const navigate = () => window.location.assign(url);
    card.addEventListener('click', navigate);
    card.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      navigate();
    });
  };

  const initializeCollapsibles = (root) => {
    root.querySelectorAll('[data-replo-collapsible="true"]').forEach((item) => {
      const trigger = item.querySelector(':scope > [role="button"]');
      if (!trigger || trigger.dataset.hrMerchBound === 'true') return;
      const contentId = trigger.getAttribute('aria-controls');
      const content = contentId ? root.querySelector(`#${CSS.escape(contentId)}`) : trigger.nextElementSibling;
      if (!content) return;
      trigger.dataset.hrMerchBound = 'true';
      const setOpen = (open) => {
        trigger.setAttribute('aria-expanded', String(open));
        item.dataset.state = open ? 'open' : 'closed';
        content.dataset.state = open ? 'open' : 'closed';
        content.hidden = !open;
      };
      setOpen(!content.hidden);
      const toggle = () => setOpen(content.hidden);
      trigger.addEventListener('click', toggle);
      trigger.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        toggle();
      });
    });
  };

  const formatMoney = (cents, moneyFormat) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, moneyFormat);
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(cents / 100);
  };

  const initializeCarousel = (root, config) => {
    const carousel = root.querySelector('[data-replo-carousel="true"]');
    const images = Array.isArray(config.images) ? config.images.filter((image) => image?.src) : [];
    if (!carousel || !images.length) return null;

    const tracks = [...carousel.querySelectorAll('[data-replo-part="slide-track"]')];
    const templates = tracks.map((track) => track.querySelector(':scope > [data-replo-part="slide"]'));
    if (tracks.length < 2 || templates.some((template) => !template)) return null;

    const previousButton = carousel.querySelector('[data-replo-part="nav-button"][data-direction="previous"]');
    const nextButton = carousel.querySelector('[data-replo-part="nav-button"][data-direction="next"]');
    const status = carousel.querySelector('[aria-live]');
    const disabledClass = new Map(
      [previousButton, nextButton].filter(Boolean).map((button) => {
        const classes = [...button.classList];
        return [button, classes.length > 1 ? classes.at(-1) : null];
      })
    );
    const thumbnailTrack = tracks[1];
    const thumbnailActiveClass = 'r-1w8cfke';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const setSlideImage = (slide, image) => {
      const picture = slide.querySelector('picture');
      if (picture) picture.style.setProperty('--replo-attributes-currentitem', `url(${JSON.stringify(image.src)})`);
      slide.querySelectorAll('source').forEach((source) => {
        const media = source.media || '';
        let srcset = image.src1800 || image.src;
        if (/max-width:\s*640px/.test(media)) srcset = image.src820 || image.src;
        else if (/max-width:\s*1024px/.test(media)) srcset = image.src1024 || image.src;
        source.setAttribute('srcset', srcset);
      });
      const imageElement = slide.querySelector('img');
      if (imageElement) {
        imageElement.src = image.src;
        imageElement.alt = image.alt || config.product?.title || '';
      }
    };

    tracks.forEach((track, trackIndex) => {
      const template = templates[trackIndex];
      const rootId = track.getAttribute('data-replo-root-id') || `hr-merch-carousel-${trackIndex}`;
      const slides = images.map((image, imageIndex) => {
        const slide = template.cloneNode(true);
        slide.setAttribute('data-slide-index', String(imageIndex));
        slide.setAttribute('data-slide-source-index', String(imageIndex));
        slide.setAttribute('data-slide-id', `${rootId}::slide:${imageIndex}`);
        slide.id = `${rootId}::slide:${imageIndex}:hr-merch`;
        slide.removeAttribute('data-is-active');
        slide.removeAttribute('data-is-target');
        slide.removeAttribute('data-is-cloned');
        slide.removeAttribute('aria-hidden');
        slide.setAttribute('tabindex', '-1');
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', `Slide ${imageIndex + 1}`);
        slide.firstElementChild?.classList.remove(thumbnailActiveClass);
        setSlideImage(slide, image);

        if (track === thumbnailTrack) {
          slide.dataset.hrMerchThumbnail = 'true';
          slide.setAttribute('role', 'button');
          slide.setAttribute('tabindex', '0');
          slide.setAttribute('aria-label', `Show product image ${imageIndex + 1} of ${images.length}`);
        }
        return slide;
      });
      track.replaceChildren(...slides);
    });

    let activeIndex = -1;
    let transitionTimer;
    let resizeFrame;

    const updateTrackPositions = (animate) => {
      const shouldAnimate = animate && !reducedMotion.matches;
      window.clearTimeout(transitionTimer);
      tracks.forEach((track) => {
        const activeSlide = track.children[activeIndex];
        if (!activeSlide) return;
        let offset = activeSlide.offsetLeft;
        if (track === thumbnailTrack && track.firstElementChild) {
          offset += Math.max(0, activeSlide.offsetWidth - track.firstElementChild.offsetWidth);
        }
        track.style.transitionDuration = shouldAnimate ? '250ms' : '0ms';
        track.style.transitionProperty = shouldAnimate ? 'transform' : 'none';
        track.style.transitionTimingFunction = shouldAnimate ? 'ease' : '';
        track.style.transform = `translate3d(-${offset}px, 0px, 0px)`;
      });
      if (shouldAnimate) {
        transitionTimer = window.setTimeout(() => {
          tracks.forEach((track) => {
            track.style.transitionDuration = '0ms';
            track.style.transitionProperty = 'none';
            track.style.transitionTimingFunction = '';
          });
        }, 260);
      }
    };

    const setButtonDisabled = (button, disabled) => {
      if (!button) return;
      button.disabled = disabled;
      const modifier = disabledClass.get(button);
      if (modifier) button.classList.toggle(modifier, disabled);
    };

    const setActive = (requestedIndex, animate = true) => {
      const nextIndex = Math.max(0, Math.min(images.length - 1, requestedIndex));
      const changed = nextIndex !== activeIndex;
      activeIndex = nextIndex;
      tracks.forEach((track) => {
        [...track.children].forEach((slide, slideIndex) => {
          const active = slideIndex === activeIndex;
          if (active) {
            slide.setAttribute('data-is-active', 'true');
            slide.setAttribute('data-is-target', 'true');
          } else {
            slide.removeAttribute('data-is-active');
            slide.removeAttribute('data-is-target');
          }
          if (track === tracks[0]) {
            if (active) slide.removeAttribute('aria-hidden');
            else slide.setAttribute('aria-hidden', 'true');
          } else if (track === thumbnailTrack) {
            slide.setAttribute('aria-current', active ? 'true' : 'false');
            slide.firstElementChild?.classList.toggle(thumbnailActiveClass, active);
          }
        });
      });
      if (status) status.textContent = `Slide ${activeIndex + 1} of ${images.length}`;
      setButtonDisabled(previousButton, activeIndex === 0);
      setButtonDisabled(nextButton, activeIndex === images.length - 1);
      updateTrackPositions(animate && changed);
    };

    const imageIndexForVariant = (variant) => {
      const position = Number(variant?.featured_image?.position || variant?.featured_media?.position);
      if (Number.isInteger(position) && position > 0 && position <= images.length) return position - 1;
      const featuredUrl = variant?.featured_image?.src || variant?.featured_media?.preview_image?.src;
      if (!featuredUrl) return 0;
      const imagePath = (url) => {
        try {
          return new URL(url, window.location.href).pathname;
        } catch (error) {
          return String(url).split('?')[0];
        }
      };
      const match = images.findIndex((image) => imagePath(image.src) === imagePath(featuredUrl));
      return match >= 0 ? match : 0;
    };

    previousButton?.addEventListener('click', () => setActive(activeIndex - 1));
    nextButton?.addEventListener('click', () => setActive(activeIndex + 1));

    [...thumbnailTrack.children].forEach((thumbnail, thumbnailIndex) => {
      const activate = () => setActive(thumbnailIndex);
      thumbnail.addEventListener('click', activate);
      thumbnail.addEventListener('keydown', (event) => {
        let nextIndex = thumbnailIndex;
        if (event.key === 'Enter' || event.key === ' ') activate();
        else if (event.key === 'ArrowLeft') nextIndex = Math.max(0, thumbnailIndex - 1);
        else if (event.key === 'ArrowRight') nextIndex = Math.min(images.length - 1, thumbnailIndex + 1);
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = images.length - 1;
        else return;
        event.preventDefault();
        if (nextIndex !== thumbnailIndex) {
          setActive(nextIndex);
          thumbnailTrack.children[nextIndex]?.focus();
        }
      });
    });

    carousel.addEventListener('keydown', (event) => {
      if (event.target.closest('[data-hr-merch-thumbnail]')) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      setActive(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });

    const mainViewport = tracks[0].parentElement;
    let touchStart;
    mainViewport?.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }, { passive: true });
    mainViewport?.addEventListener('touchend', (event) => {
      if (!touchStart || !event.changedTouches.length) return;
      const deltaX = event.changedTouches[0].clientX - touchStart.x;
      const deltaY = event.changedTouches[0].clientY - touchStart.y;
      touchStart = null;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      setActive(activeIndex + (deltaX < 0 ? 1 : -1));
    }, { passive: true });
    mainViewport?.addEventListener('touchcancel', () => { touchStart = null; }, { passive: true });

    const schedulePositionUpdate = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => updateTrackPositions(false));
    };
    let resizeObserver;
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(schedulePositionUpdate);
      tracks.forEach((track) => resizeObserver.observe(track.parentElement));
    } else {
      window.addEventListener('resize', schedulePositionUpdate);
    }

    return {
      resizeObserver,
      selectVariant(variant, animate) {
        setActive(imageIndexForVariant(variant), animate);
      },
    };
  };

  const initializeProduct = (root, config, carousel) => {
    const product = config.product;
    if (!product?.variants?.length) return;

    const form = root.querySelector('form[action="/cart/add"]');
    const variantInput = form?.querySelector('[name="id"]');
    const addButton = root.querySelector('[data-replo-add-product-variant-to-cart]');
    const price = root.querySelector('[data-replo-price="true"] p');
    const listboxes = [...root.querySelectorAll('[data-replo-option-select="true"]')];
    let selectedVariant = product.variants.find((variant) => String(variant.id) === String(variantInput?.value)) || product.variants[0];
    const selectedOptions = [...(selectedVariant.options || [])];
    const claimedIndexes = new Set();
    const optionValuesAt = (index) => [...new Set(product.variants.map((variant) => variant.options?.[index]).filter(Boolean))]
      .sort((a, b) => b.length - a.length);
    const choiceMatchesValue = (choice, value) => {
      const text = choice.textContent.trim();
      return text === value || text.endsWith(value);
    };

    const listboxState = listboxes.map((listbox, fallbackIndex) => {
      const choices = [...listbox.querySelectorAll('[role="option"]')];
      let optionIndex = (product.options || []).findIndex((_, index) => {
        if (claimedIndexes.has(index)) return false;
        const allowed = optionValuesAt(index);
        return choices.every((choice) => allowed.some((value) => choiceMatchesValue(choice, value)));
      });
      if (optionIndex < 0) optionIndex = fallbackIndex;
      claimedIndexes.add(optionIndex);
      const activeChoice = choices.find((choice) => choice.getAttribute('aria-selected') === 'true');
      const inactiveChoice = choices.find((choice) => choice.getAttribute('aria-selected') !== 'true');
      const activeTokens = activeChoice && inactiveChoice
        ? [...activeChoice.classList].filter((token) => !inactiveChoice.classList.contains(token))
        : [];
      const allowedValues = optionValuesAt(optionIndex);
      const valueForChoice = (choice) => allowedValues.find((value) => choiceMatchesValue(choice, value)) || choice.textContent.trim();
      return { choices, optionIndex, activeTokens, valueForChoice };
    });

    const setError = (message = '') => {
      if (!addButton) return;
      let error = addButton.parentElement.querySelector('.hr-product-merch-error');
      if (!message) {
        error?.remove();
        return;
      }
      if (!error) {
        error = document.createElement('div');
        error.className = 'hr-product-merch-error';
        error.setAttribute('role', 'alert');
        addButton.insertAdjacentElement('afterend', error);
      }
      error.textContent = message;
    };

    let hasInitializedCarousel = false;
    const updateState = () => {
      selectedVariant = product.variants.find((variant) =>
        (variant.options || []).every((value, index) => value === selectedOptions[index])
      );
      if (variantInput && selectedVariant) variantInput.value = selectedVariant.id;
      if (price && selectedVariant) price.textContent = formatMoney(selectedVariant.price, config.moneyFormat);
      if (selectedVariant && carousel) {
        carousel.selectVariant(selectedVariant, hasInitializedCarousel);
        hasInitializedCarousel = true;
      }
      if (addButton) {
        const available = Boolean(selectedVariant?.available);
        addButton.disabled = !available;
        addButton.setAttribute('aria-disabled', String(!available));
        const label = addButton.querySelector('p');
        if (label) label.textContent = available ? 'Add To Cart' : 'Sold Out';
      }
    };

    listboxState.forEach(({ choices, optionIndex, activeTokens, valueForChoice }) => {
      choices.forEach((choice) => {
        const select = () => {
          selectedOptions[optionIndex] = valueForChoice(choice);
          choices.forEach((candidate) => {
            const active = candidate === choice;
            candidate.setAttribute('aria-selected', String(active));
            activeTokens.forEach((token) => candidate.classList.toggle(token, active));
          });
          updateState();
        };
        choice.addEventListener('click', select);
        choice.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          select();
        });
      });
    });

    if (addButton) {
      let loading = false;
      const originalMarkup = addButton.innerHTML;
      const setLoading = (isLoading) => {
        loading = isLoading;
        addButton.setAttribute('aria-busy', String(isLoading));
        if (isLoading) {
          addButton.innerHTML = '<span class="hr-product-merch-loading"><span>LOADING</span><span class="hr-product-merch-loading__spinner" aria-hidden="true"></span></span>';
        } else {
          addButton.innerHTML = originalMarkup;
          updateState();
        }
      };

      addButton.addEventListener('click', async (event) => {
        event.preventDefault();
        if (loading || !selectedVariant?.available) return;
        setError();
        setLoading(true);
        try {
          const cart = document.querySelector('cart-notification, cart-drawer');
          const formData = new FormData();
          formData.append('id', selectedVariant.id);
          formData.append('quantity', '1');
          if (cart?.getSectionsToRender) {
            formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
            formData.append('sections_url', window.location.pathname);
          }
          const response = await fetch(config.routes?.cartAdd || '/cart/add', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
          const payload = await response.json();
          if (!response.ok || payload.status) throw new Error(payload.description || payload.message || 'Unable to add this item.');

          if (typeof window.EliteCart?.openDrawerWithDataRefresh === 'function') {
            await window.EliteCart.openDrawerWithDataRefresh();
          } else if (cart?.renderContents && payload.sections) {
            cart.renderContents(payload);
          } else {
            window.location.assign(config.routes?.cart || '/cart');
          }
        } catch (error) {
          setError(error.message || 'Unable to add this item. Please try again.');
        } finally {
          setLoading(false);
        }
      });
    }

    updateState();
  };

  const initialize = (root) => {
    if (!root || initializedRoots.has(root)) return;
    initializedRoots.add(root);
    const config = parseConfig(root);
    const carousel = initializeCarousel(root, config);
    initializeProduct(root, config, carousel);
    initializeCollapsibles(root);
    setCardLink(root, '2ae06af7-656a-4980-8e61-9727de7a5750', config.routes?.shirts, 'Shop shirts');
    setCardLink(root, 'd28c9dfe-8a24-4d50-bf76-3e84b47d7f72', config.routes?.hats, 'Shop hats');
    setCardLink(root, 'f9e600ff-5c34-48da-bb80-2f9f0187b5db', config.routes?.hoodies, 'Shop hoodies');
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initialize(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
